import type { TFunction } from 'i18next'
import type { IconType } from 'react-icons'
import {
  FiSettings, FiUsers, FiBook, FiTarget, FiCheckCircle, FiFlag,
  FiBarChart2, FiTrendingUp, FiActivity, FiHeart, FiMousePointer, FiMap,
  FiCompass, FiSearch, FiLayers, FiMail, FiShare2, FiCalendar, FiMessageSquare,
  FiDollarSign, FiFileText,
  FiCode, FiServer, FiZap, FiAlertTriangle, FiDatabase, FiPackage,
  FiClipboard, FiImage,
  FiDroplet, FiShield, FiKey, FiClock, FiThumbsUp,
} from 'react-icons/fi'
import type { NavItemData } from './useNavigationItems'

export interface AdminCategory {
  title: string
  icon: IconType
  slug: string
  items: NavItemData[]
}

export function getAdminCategories(t: TFunction): AdminCategory[] {
  return [
    {
      title: 'Core Admin',
      icon: FiSettings,
      slug: 'core-admin',
      items: [
        { to: '/admin/users', icon: FiUsers, label: t('nav.userManagement') },
        { to: '/admin/vendors', icon: FiPackage, label: 'Vendors' },
        { to: '/admin/schools', icon: FiBook, label: t('nav.schools') },
        { to: '/admin/challenges', icon: FiTarget, label: t('nav.challengeReview') },
        { to: '/admin/challenge-series', icon: FiLayers, label: 'Challenge Series' },
        { to: '/admin/orders', icon: FiDollarSign, label: 'Orders' },
        { to: '/admin/coupons', icon: FiCalendar, label: 'Coupons' },
        { to: '/admin/reviews', icon: FiMessageSquare, label: 'Review Moderation' },
        { to: '/admin/verification', icon: FiCheckCircle, label: t('nav.verificationQueue') },
        { to: '/admin/settings', icon: FiSettings, label: t('nav.platformSettings') },
        { to: '/admin/feature-flags', icon: FiFlag, label: 'Feature Flags' },
      ],
    },
    {
      title: 'Product',
      icon: FiClipboard,
      slug: 'product',
      items: [
        { to: '/admin/tracker', icon: FiClipboard, label: 'Product Tracker' },
        { to: '/admin/doc-feedback', icon: FiMessageSquare, label: 'Doc Feedback' },
        { to: '/admin/feature-requests', icon: FiThumbsUp, label: 'Feature Requests' },
      ],
    },
    {
      title: 'Analytics',
      icon: FiBarChart2,
      slug: 'analytics',
      items: [
        { to: '/admin/executive-dashboard', icon: FiBarChart2, label: 'Executive Dashboard' },
        { to: '/admin/analytics', icon: FiBarChart2, label: t('nav.analytics') },
        { to: '/admin/north-star', icon: FiTrendingUp, label: 'North Star' },
        { to: '/admin/feature-usage', icon: FiActivity, label: 'Feature Usage' },
        { to: '/admin/cohort-analysis', icon: FiUsers, label: 'Cohort Analysis' },
        { to: '/admin/user-health', icon: FiHeart, label: 'User Health' },
        { to: '/admin/heatmaps', icon: FiMousePointer, label: 'Heatmaps' },
        { to: '/admin/session-replay', icon: FiActivity, label: 'Session Replay' },
        { to: '/admin/funnels', icon: FiTrendingUp, label: 'Funnels' },
        { to: '/admin/user-journeys', icon: FiMap, label: 'User Journeys' },
        { to: '/admin/ab-tests', icon: FiTarget, label: 'A/B Tests' },
      ],
    },
    {
      title: 'Growth & Marketing',
      icon: FiTrendingUp,
      slug: 'growth-marketing',
      items: [
        { to: '/admin/campaigns', icon: FiCompass, label: 'Campaigns' },
        { to: '/admin/lead-scoring', icon: FiTrendingUp, label: 'Lead Scoring' },
        { to: '/admin/seo', icon: FiSearch, label: 'SEO Dashboard' },
        { to: '/admin/landing-pages', icon: FiLayers, label: 'Landing Pages' },
        { to: '/admin/email-templates', icon: FiMail, label: 'Email Templates' },
        { to: '/admin/social-proof', icon: FiShare2, label: 'Social Proof' },
        { to: '/admin/referral-program', icon: FiShare2, label: 'Referrals' },
        { to: '/admin/trial-manager', icon: FiCalendar, label: 'Trial Manager' },
        { to: '/admin/competitive-intel', icon: FiTarget, label: 'Competitive Intel' },
        { to: '/admin/surveys', icon: FiMessageSquare, label: 'In-App Surveys' },
      ],
    },
    {
      title: 'Sales & Finance',
      icon: FiDollarSign,
      slug: 'sales-finance',
      items: [
        { to: '/admin/investor-data-room', icon: FiFileText, label: 'Investor Data Room' },
        { to: '/admin/sales-decks', icon: FiLayers, label: 'Sales Decks' },
        { to: '/admin/pricing', icon: FiDollarSign, label: 'Pricing' },
        { to: '/admin/roi-calculator', icon: FiDollarSign, label: 'ROI Calculator' },
        { to: '/admin/budget', icon: FiDollarSign, label: 'Budget Tracker' },
        { to: '/admin/costs', icon: FiDollarSign, label: 'Cost Tracker' },
        { to: '/admin/okrs', icon: FiTarget, label: 'OKRs' },
      ],
    },
    {
      title: 'Content',
      icon: FiBook,
      slug: 'content',
      items: [
        { to: '/admin/knowledge-base', icon: FiBook, label: 'Knowledge Base' },
        { to: '/admin/content-calendar', icon: FiCalendar, label: 'Content Calendar' },
        { to: '/admin/release-notes', icon: FiFlag, label: 'Release Notes' },
        { to: '/admin/wiki', icon: FiBook, label: 'Internal Wiki' },
        { to: '/admin/exports', icon: FiShare2, label: 'Export Anything' },
      ],
    },
    {
      title: 'Schools',
      icon: FiUsers,
      slug: 'schools',
      items: [
        { to: '/admin/multi-school', icon: FiLayers, label: 'Multi-School' },
        { to: '/admin/teacher-effectiveness', icon: FiCheckCircle, label: 'Teacher Effectiveness' },
        { to: '/admin/onboarding-wizards', icon: FiMap, label: 'Onboarding Wizards' },
        { to: '/admin/white-label', icon: FiDroplet, label: 'White-Label' },
      ],
    },
    {
      title: 'Engineering',
      icon: FiCode,
      slug: 'engineering',
      items: [
        { to: '/admin/deployments', icon: FiServer, label: 'Deployments' },
        { to: '/admin/api-playground', icon: FiCode, label: 'API Playground' },
        { to: '/admin/performance', icon: FiZap, label: 'Performance' },
        { to: '/admin/error-dashboard', icon: FiAlertTriangle, label: 'Error Dashboard' },
        { to: '/admin/uptime', icon: FiActivity, label: 'Uptime Monitor' },
        { to: '/admin/environments', icon: FiServer, label: 'Environments' },
        { to: '/admin/schema-diff', icon: FiDatabase, label: 'Schema Diff' },
        { to: '/admin/flag-rollout', icon: FiFlag, label: 'Flag Rollout' },
        { to: '/admin/integrations', icon: FiPackage, label: 'Integrations' },
        { to: 'https://docs.docurious.com', icon: FiFileText, label: 'Documentation', external: true },
        { to: 'https://storybook.docurious.com', icon: FiLayers, label: 'Storybook', external: true },
      ],
    },
    {
      title: 'QA & Testing',
      icon: FiCheckCircle,
      slug: 'qa-testing',
      items: [
        { to: '/admin/coverage-map', icon: FiCheckCircle, label: 'Coverage Map' },
        { to: '/admin/test-scenarios', icon: FiClipboard, label: 'Test Scenarios' },
        { to: '/admin/mock-data', icon: FiDatabase, label: 'Mock Data' },
        { to: '/admin/chaos-mode', icon: FiZap, label: 'Chaos Mode' },
        { to: '/admin/visual-regression', icon: FiImage, label: 'Visual Regression' },
        { to: '/admin/bug-reports', icon: FiAlertTriangle, label: 'Bug Reports' },
        { to: '/admin/accessibility', icon: FiActivity, label: 'Accessibility Audit' },
      ],
    },
    {
      title: 'Design & UX',
      icon: FiDroplet,
      slug: 'design-ux',
      items: [
        { to: '/admin/theme-editor', icon: FiDroplet, label: 'Theme Editor' },
        { to: '/admin/token-changelog', icon: FiDroplet, label: 'Token Changelog' },
        { to: '/admin/responsive-preview', icon: FiImage, label: 'Responsive Preview' },
        { to: '/admin/flow-recorder', icon: FiActivity, label: 'Flow Recorder' },
        { to: '/admin/annotations', icon: FiMessageSquare, label: 'Annotations' },
        { to: '/admin/component-showcase', icon: FiPackage, label: 'Components' },
        { to: '/admin/tour-builder', icon: FiMap, label: 'Tour Builder' },
        { to: '/admin/feature-requests', icon: FiThumbsUp, label: 'Feature Requests' },
      ],
    },
    {
      title: 'Legal & Compliance',
      icon: FiShield,
      slug: 'legal-compliance',
      items: [
        { to: '/admin/compliance', icon: FiFlag, label: 'Compliance' },
        { to: '/admin/consent', icon: FiShield, label: 'Consent Dashboard' },
        { to: '/admin/terms-versions', icon: FiFileText, label: 'Terms Versions' },
        { to: '/admin/pii-scanner', icon: FiShield, label: 'PII Scanner' },
        { to: '/admin/data-requests', icon: FiFileText, label: 'Data Requests' },
      ],
    },
    {
      title: 'Support',
      icon: FiMessageSquare,
      slug: 'support',
      items: [
        { to: '/admin/support-actions', icon: FiKey, label: 'Support Actions' },
        { to: '/admin/support-history', icon: FiClock, label: 'Support History' },
      ],
    },
  ]
}
